// SPDX-License-Identifier: Elastic-2.0
/**
 * RoutinesListPage — content of `/routines` (T-026).
 *
 * Per task spec § 3:
 *   - Page header: "Your routines" + count + caption
 *     ("Routines are created when you accept a suggestion.").
 *   - List of RoutineCard, sorted active-first then alphabetical (sort
 *     lives in `sortRoutines()` so tests share the same ordering).
 *   - Loading: 4 skeleton cards.
 *   - Empty state: title + hint + arrow CTA → /suggestions.
 *   - Error: banner + retry button.
 *
 * Per AC:
 *   - Trigger → toast "Routine triggered" + invalidate `["routines"]`.
 *   - Toggle → optimistic patch in the hook + toast on failure.
 *   - Delete → in-card confirm row → mutate → toast "Routine deleted"
 *     + the hook removes the row from the list optimistically.
 *
 * Per F-300 caveat: `useDeleteRoutine` / `useToggleRoutine` hit endpoints
 * that aren't yet shipped (T-015 stubs return 501). When they fail we
 * surface an inline `<Banner>` with a CTA pointing the user at the HA
 * advanced editor — same fallback NewRoutineStub uses.
 */
import { useMemo, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Banner } from "@/components/Banner/Banner";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon/Icon";
import { Page } from "@/components/Page/Page";
import { useToast } from "@/components/Toast/useToast";
import { ROUTES } from "@/routing/routes";

import type { Routine } from "@/api/gateway/models/Routine";

import { RoutineCard } from "./RoutineCard";
import {
  sortRoutines,
  useDeleteRoutine,
  useRoutines,
  useToggleRoutine,
  useTriggerRoutine,
} from "./useRoutines";

import styles from "./RoutinesListPage.module.css";

const SKELETON_COUNT = 4;

export function RoutinesListPage(): ReactElement {
  const { t } = useTranslation();
  const { show: showToast } = useToast();
  const routinesQuery = useRoutines();
  const triggerMutation = useTriggerRoutine();
  const toggleMutation = useToggleRoutine();
  const deleteMutation = useDeleteRoutine();

  // Track which routine id is the focus of a mutation right now so the
  // per-card busy flag can disable only that card's controls.
  const [busyId, setBusyId] = useState<string | null>(null);

  const sorted: Routine[] = useMemo(
    () => sortRoutines(routinesQuery.data?.routines ?? []),
    [routinesQuery.data],
  );

  const handleTrigger = (routine: Routine): void => {
    setBusyId(routine.id);
    triggerMutation.mutate(
      { id: routine.id },
      {
        onSuccess: () => {
          showToast({
            kind: "success",
            message: t("routines.trigger_success"),
          });
        },
        onError: () => {
          showToast({
            kind: "error",
            message: t("dashboard.devices.control_failed"),
          });
        },
        onSettled: () => setBusyId(null),
      },
    );
  };

  const handleToggle = (routine: Routine, nextActive: boolean): void => {
    setBusyId(routine.id);
    toggleMutation.mutate(
      { id: routine.id, active: nextActive },
      {
        onError: () => {
          showToast({
            kind: "error",
            message: t("dashboard.devices.control_failed"),
          });
        },
        onSettled: () => setBusyId(null),
      },
    );
  };

  const handleDelete = (routine: Routine): void => {
    setBusyId(routine.id);
    deleteMutation.mutate(
      { id: routine.id },
      {
        onSuccess: () => {
          showToast({
            kind: "success",
            message: t("routines.delete_success"),
          });
        },
        onError: () => {
          showToast({
            kind: "error",
            message: t("dashboard.devices.control_failed"),
          });
        },
        onSettled: () => setBusyId(null),
      },
    );
  };

  const title = t("routines.page_title");

  return (
    <Page title={title} data-testid="routines-page">
      <header className={styles.header} data-testid="routines-header">
        <div className={styles.headerRow}>
          {!routinesQuery.isLoading && routinesQuery.data ? (
            <span className={styles.count} data-testid="routines-count">
              {sorted.length}
            </span>
          ) : null}
        </div>
        <p className={styles.caption}>{t("routines.page_caption")}</p>
      </header>

      {routinesQuery.isLoading ? (
        <div
          className={styles.stack}
          data-testid="routines-loading"
          aria-label={t("routines.loading")}
          aria-busy="true"
        >
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className={styles.skeleton}
              aria-hidden="true"
              data-testid="routines-skeleton"
            />
          ))}
        </div>
      ) : routinesQuery.isError ? (
        <div data-testid="routines-error">
          <Banner kind="error" title={t("routines.error_title")} message={t("errors.generic")} />
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void routinesQuery.refetch();
              }}
              data-testid="routines-retry"
            >
              {t("routines.error_retry")}
            </Button>
          </div>
        </div>
      ) : sorted.length === 0 ? (
        <div className={styles.empty} data-testid="routines-empty">
          <h2 className={styles.emptyTitle}>{t("routines.empty_title")}</h2>
          <p className={styles.emptyHint}>{t("routines.empty_hint")}</p>
          <Link
            to={ROUTES.SUGGESTIONS}
            className={styles.stubLink}
            data-testid="routines-empty-cta"
          >
            <span>{t("routines.go_to_suggestions")}</span>
            <Icon name="arrow_right" size={16} />
          </Link>
        </div>
      ) : (
        <div className={styles.stack} data-testid="routines-list">
          {sorted.map((routine) => (
            <RoutineCard
              key={routine.id}
              routine={routine}
              onTrigger={handleTrigger}
              onToggle={handleToggle}
              onDelete={handleDelete}
              busy={busyId === routine.id}
            />
          ))}
        </div>
      )}
    </Page>
  );
}

export default RoutinesListPage;
